import type { TransactionsMetaResponse } from '@/lib/api';
import { buildCategoryTree, categoryPathLabel } from '@/lib/categories/tree';
import {
  type CategoryChipStyle,
  FALLBACK_CATEGORY,
  categoryChipStyleFromColor,
  resolveCategoryColor,
} from '@/lib/transactions/categories';
import type { CategoryOverviewRow, CustomCategorySummary, ParentCategory, Transaction } from '@/types';
import type { ScreenTransaction } from './types';

/**
 * Per-category metadata derived from server data (taxonomy, overview rows or
 * the period's transaction meta — see the builders below). Threaded down to
 * every consumer that needs to render a category chip / tile / breakdown
 * segment / filter row. Colors always come from `resolveCategoryColor`, so the
 * same ref renders the same color on every page.
 */
export interface CategoryMeta {
  id: string;
  label: string;
  iconName: string;
  color: string;
  /**
   * Breadcrumb label for leaves 3+ levels deep — `"Restaurantes › Delivery"`,
   * root omitted. Only set by the taxonomy builder (the transactions meta DTO
   * carries leaf + root, so the middle level isn't recoverable there yet).
   * Render `pathLabel ?? label` wherever a bare 3rd-level name is ambiguous.
   */
  pathLabel?: string;
}

export type CategoryIndex = ReadonlyMap<string, CategoryMeta>;

export const UNCATEGORIZED_META: CategoryMeta = {
  id: 'uncategorized',
  label: 'Outros',
  iconName: 'circle-dashed',
  color: FALLBACK_CATEGORY.color,
};

export function getCategoryMeta(index: CategoryIndex, id: string | null | undefined): CategoryMeta {
  if (!id) return UNCATEGORIZED_META;
  return index.get(id) ?? UNCATEGORIZED_META;
}

/**
 * A raw API transaction's category as the index knows it, or — for a ref the index lacks — as the transaction itself
 * carries it (the send flow works on the unadapted transaction).
 */
export function transactionCategoryMeta(index: CategoryIndex, transaction: Pick<Transaction, 'customCategoryId' | 'categoryId' | 'categoryTranslated' | 'category' | 'categoryIcon'>): CategoryMeta {
  const id = transaction.customCategoryId ?? transaction.categoryId;
  const known = id ? index.get(id) : undefined;
  if (known) return known;
  return {
    id: id ?? 'uncategorized',
    label: transaction.categoryTranslated ?? transaction.category ?? 'Outros',
    iconName: transaction.categoryIcon ?? 'circle-dashed',
    color: resolveCategoryColor(id ?? 'uncategorized'),
  };
}

/**
 * The category OTHER people see for this transaction once it is in a space: the API stores the taxonomy category
 * (`sharedCategoryId`), never the payer's custom one — a custom category is private (its name, color and existence).
 * When a custom category is assigned, the transaction's own label fields may describe it, so they are not trusted.
 */
export function sharedTransactionCategoryMeta(index: CategoryIndex, transaction: Pick<Transaction, 'customCategoryId' | 'categoryId' | 'categoryTranslated' | 'category' | 'categoryIcon'>): CategoryMeta {
  const labels = transaction.customCategoryId
    ? {}
    : { categoryTranslated: transaction.categoryTranslated, category: transaction.category, categoryIcon: transaction.categoryIcon };
  return transactionCategoryMeta(index, { categoryId: transaction.categoryId, customCategoryId: null, ...labels });
}

export type { CategoryChipStyle };

export function categoryChipStyleFromMeta(meta: CategoryMeta): CategoryChipStyle {
  return categoryChipStyleFromColor(meta.color);
}

function upsertInto(
  index: Map<string, CategoryMeta>,
  id: string,
  label: string,
  iconName?: string | null,
  customColor?: string | null,
  pathLabel?: string,
) {
  if (!index.has(id)) {
    index.set(id, {
      id,
      label,
      iconName: iconName ?? 'circle-dashed',
      // A blank/absent custom color means "no color" — resolveCategoryColor
      // falls back to the shared hierarchical palette.
      color: resolveCategoryColor(id, 'custom', customColor),
      ...(pathLabel ? { pathLabel } : {}),
    });
  }
}

/**
 * CategoryIndex from the picker taxonomy + the Categorias overview rows (the
 * transactions screen builds its own from server meta via
 * `buildCategoryIndexFromServerMeta`; here the tree is enough). Reconstructing
 * the tree gives 3rd-level leaves their breadcrumb `pathLabel` and the icon
 * fallback of their immediate parent.
 */
export function buildCategoryIndexFromTaxonomy(
  parents: readonly ParentCategory[] | undefined,
  rows: readonly CategoryOverviewRow[],
  customCategories?: readonly CustomCategorySummary[],
): CategoryIndex {
  const index = new Map<string, CategoryMeta>();

  // Seeded first, as in the server-meta builder: the user's picked color wins over the palette.
  for (const custom of customCategories ?? []) {
    upsertInto(index, custom.id, custom.name, custom.icon, custom.color);
  }
  const tree = buildCategoryTree(parents);
  for (const node of tree.byId.values()) {
    upsertInto(
      index,
      node.id,
      node.label,
      node.iconName,
      null,
      node.depth >= 3 ? categoryPathLabel(tree, node.id) ?? undefined : undefined,
    );
  }
  for (const row of rows) {
    // Custom categories keep the user-picked color; Pluggy rows use the shared palette.
    upsertInto(index, row.categoryRef, row.name, row.icon, row.source === 'custom' ? row.color : null);
  }

  return index;
}

/**
 * CategoryIndex for the transactions screen: backend meta is the source of
 * truth for labels/icons; custom categories are seeded first so the user's
 * picked color wins over the palette for their cuid refs.
 */
export function buildCategoryIndexFromServerMeta(
  meta: Pick<TransactionsMetaResponse, 'categories' | 'categoryFacets'> | undefined,
  txs: readonly ScreenTransaction[],
  customCategories?: readonly CustomCategorySummary[],
): CategoryIndex {
  const index = new Map<string, CategoryMeta>();

  for (const custom of customCategories ?? []) {
    upsertInto(index, custom.id, custom.name, custom.icon, custom.color);
  }
  for (const m of meta?.categories ?? []) {
    upsertInto(index, m.id, m.label, m.iconName);
    upsertInto(index, m.parentCategoryId, m.parentLabel, m.parentIconName);
  }
  for (const facet of meta?.categoryFacets ?? []) {
    upsertInto(index, facet.id, facet.label, facet.iconName);
    upsertInto(index, facet.parentCategoryId, facet.parentLabel, facet.parentIconName);
  }
  for (const t of txs) {
    const src = t.source;
    upsertInto(
      index,
      t.categoryId,
      src.categoryTranslated ?? src.category ?? src.parentCategoryTranslated ?? src.parentCategory ?? 'Outros',
      src.categoryIcon ?? src.parentCategoryIcon,
    );
    const parentId = t.parentCategoryId ?? t.categoryId;
    upsertInto(
      index,
      parentId,
      src.parentCategoryTranslated ?? src.parentCategory ?? src.categoryTranslated ?? src.category ?? 'Outros',
      src.parentCategoryIcon ?? src.categoryIcon,
    );
  }
  index.set(UNCATEGORIZED_META.id, UNCATEGORIZED_META);

  return index;
}
